import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { cn } from '@/lib/utils';

interface PushNotificationToggleProps {
  /**
   * Se true, usa a API do portal (pacientes).
   */
  isPortal?: boolean;

  /**
   * Classes CSS adicionais.
   */
  className?: string;

  /**
   * Se deve mostrar apenas o ícone (sem popover).
   */
  iconOnly?: boolean;
}

export function PushNotificationToggle({
  isPortal = false,
  className,
  iconOnly = false,
}: PushNotificationToggleProps) {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
  } = usePushNotifications({ isPortal });

  // Não mostrar nada se não for suportado
  if (!isSupported) {
    return null;
  }

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  const getIcon = () => {
    if (isLoading) {
      return <Loader2 className="h-5 w-5 animate-spin" />;
    }
    if (permission === 'denied') {
      return <BellOff className="h-5 w-5 text-muted-foreground" />;
    }
    if (isSubscribed) {
      return <BellRing className="h-5 w-5 text-primary" />;
    }
    return <Bell className="h-5 w-5" />;
  };

  const getStatusText = () => {
    if (permission === 'denied') {
      return 'Notificações bloqueadas';
    }
    if (isSubscribed) {
      return 'Notificações ativadas';
    }
    return 'Notificações desativadas';
  };

  const getStatusDescription = () => {
    if (permission === 'denied') {
      return 'Você bloqueou as notificações. Para reativar, altere as permissões nas configurações do seu navegador.';
    }
    if (isSubscribed) {
      return 'Você receberá alertas e avisos importantes diretamente no navegador.';
    }
    return 'Ative para receber alertas de pacientes, tarefas e mensagens.';
  };

  if (iconOnly) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggle}
              disabled={isLoading || permission === 'denied'}
              className={cn('relative', className)}
            >
              {getIcon()}
              {isSubscribed && (
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-green-500" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{getStatusText()}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', className)}
        >
          {getIcon()}
          {isSubscribed && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-green-500" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            {getIcon()}
            <div>
              <h4 className="font-medium">{getStatusText()}</h4>
              <p className="text-sm text-muted-foreground">
                {getStatusDescription()}
              </p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          {permission !== 'denied' && (
            <Button
              variant={isSubscribed ? 'outline' : 'default'}
              className="w-full"
              onClick={handleToggle}
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSubscribed ? 'Desativar notificações' : 'Ativar notificações'}
            </Button>
          )}

          {permission === 'denied' && (
            <p className="text-xs text-muted-foreground">
              Clique no ícone de cadeado na barra de endereço do navegador e
              altere as permissões de notificação para este site.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
